import { useState } from 'react';
import { Eye, EyeOff } from 'lucide-react';
import { api } from '../api';

// 서버(auth_routes.ts)의 PasswordInput 스키마와 동일하게 유지.
const MIN_LEN = 8;

// 표시/숨김 토글이 있는 비밀번호 입력.
// showState는 부모가 관리 — 여러 필드가 각각 독립 토글되도록.
function PwInput({
  value, onChange, autoComplete, minLength, style, autoFocus, show, onToggle,
}: {
  value: string;
  onChange: (v: string) => void;
  autoComplete: string;
  minLength?: number;
  style?: React.CSSProperties;
  autoFocus?: boolean;
  show: boolean;
  onToggle: () => void;
}) {
  return (
    <div style={{ position: 'relative', ...style }}>
      <input
        type={show ? 'text' : 'password'}
        value={value}
        onChange={e => onChange(e.target.value)}
        required
        autoComplete={autoComplete}
        minLength={minLength}
        autoFocus={autoFocus}
        style={{ width: '100%', paddingRight: 36 }}
      />
      <button
        type="button"
        onClick={onToggle}
        aria-label={show ? '비밀번호 숨기기' : '비밀번호 표시'}
        title={show ? '비밀번호 숨기기' : '비밀번호 표시'}
        style={{
          position: 'absolute', right: 6, top: '50%', transform: 'translateY(-50%)',
          background: 'transparent', border: 0, cursor: 'pointer',
          color: 'var(--text-muted)', padding: 6, display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}
      >
        {show ? <EyeOff size={16} /> : <Eye size={16} />}
      </button>
    </div>
  );
}

export default function ChangePassword({ onDone }: { onDone: () => void }) {
  const [cur, setCur] = useState('');
  const [next, setNext] = useState('');
  const [confirm, setConfirm] = useState('');
  const [err, setErr] = useState<string | null>(null);
  const [okMsg, setOkMsg] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  // 표시/숨김 상태 — 필드별 독립.
  const [showCur, setShowCur] = useState(false);
  const [showNext, setShowNext] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);

  // 실시간 상태 힌트 — 입력 도중 즉시 원인 알려주기 위함.
  const nextTooShort = next.length > 0 && next.length < MIN_LEN;
  const sameAsCurrent = cur.length > 0 && next.length > 0 && next === cur;
  const mismatch = confirm.length > 0 && next !== confirm;
  const nextOk = next.length >= MIN_LEN && !sameAsCurrent;
  const confirmOk = confirm.length > 0 && confirm === next && nextOk;

  const inlineHint = (color: string, msg: string) => (
    <div style={{ fontSize: 12, marginTop: 4, color, minHeight: 16 }}>{msg}</div>
  );

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setErr(null); setOkMsg(null);
    if (next.length < MIN_LEN) { setErr(`새 비밀번호는 ${MIN_LEN}자 이상이어야 합니다.`); return; }
    if (next === cur) { setErr('현재 사용 중인 비밀번호와 다른 비밀번호를 입력해 주세요.'); return; }
    if (next !== confirm) { setErr('새 비밀번호가 일치하지 않습니다.'); return; }
    setLoading(true);
    try {
      await api('PUT', '/auth/password', { current_password: cur, new_password: next });
      setOkMsg('비밀번호가 변경되었습니다.');
      setTimeout(onDone, 800);
    } catch (e: any) {
      setErr(e?.message === 'invalid_password'
        ? '현재 비밀번호가 일치하지 않습니다.'
        : (e?.message ?? '변경 실패'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="card" style={{ maxWidth: 480 }}>
      <h2>비밀번호 변경</h2>
      <form onSubmit={submit}>
        <label>현재 비밀번호</label>
        <PwInput
          value={cur} onChange={setCur} autoComplete="current-password"
          show={showCur} onToggle={() => setShowCur(v => !v)}
          style={{ marginBottom: 12 }}
        />

        <label>새 비밀번호 ({MIN_LEN}자 이상)</label>
        <PwInput
          value={next} onChange={setNext} autoComplete="new-password" minLength={MIN_LEN}
          show={showNext} onToggle={() => setShowNext(v => !v)}
        />
        {nextTooShort
          ? inlineHint('#b91c1c', `${MIN_LEN}자 이상 입력해 주세요. (현재 ${next.length}자)`)
          : sameAsCurrent
            ? inlineHint('#b91c1c', '현재 사용 중인 비밀번호와 다른 비밀번호를 입력해 주세요.')
            : nextOk
              ? inlineHint('var(--leaf-700)', '사용 가능한 비밀번호입니다.')
              : inlineHint('var(--text-muted)', ' ')}

        <label style={{ marginTop: 12 }}>새 비밀번호 확인</label>
        <PwInput
          value={confirm} onChange={setConfirm} autoComplete="new-password"
          show={showConfirm} onToggle={() => setShowConfirm(v => !v)}
        />
        {confirm.length === 0
          ? inlineHint('var(--text-muted)', ' ')
          : mismatch
            ? inlineHint('#b91c1c', '새 비밀번호가 일치하지 않습니다.')
            : confirmOk
              ? inlineHint('var(--leaf-700)', '일치합니다.')
              : inlineHint('var(--text-muted)', ' ')}

        {err && <div className="error" style={{ marginTop: 12 }}>{err}</div>}
        {okMsg && <div className="success" style={{ marginTop: 12 }}>{okMsg}</div>}
        <button type="submit" className="btn"
                disabled={loading || !nextOk || !confirmOk || cur.length === 0}
                style={{ marginTop: 12 }}>
          {loading ? '저장 중…' : '저장'}
        </button>
      </form>
    </div>
  );
}
